/**
 * Error handling type definitions
 */

import { AxiosError } from 'axios';

/**
 * Standard API error response structure
 */
export interface ApiErrorResponse {
  detail?: string;
  message?: string;
  error?: string;
  errors?: Record<string, string[]>;
  code?: string;
}

/**
 * HTTP status codes
 */
export enum HttpStatusCode {
  // Success
  OK = 200,
  CREATED = 201,
  NO_CONTENT = 204,

  // Client Errors
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  NOT_FOUND = 404,
  METHOD_NOT_ALLOWED = 405,
  CONFLICT = 409,
  UNPROCESSABLE_ENTITY = 422,
  TOO_MANY_REQUESTS = 429,

  // Server Errors
  INTERNAL_SERVER_ERROR = 500,
  BAD_GATEWAY = 502,
  SERVICE_UNAVAILABLE = 503,
  GATEWAY_TIMEOUT = 504,
}

/**
 * Error codes used throughout the application
 */
export enum ErrorCode {
  // Authentication & Authorization
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',

  // Validation
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  INVALID_INPUT = 'INVALID_INPUT',
  MISSING_REQUIRED_FIELD = 'MISSING_REQUIRED_FIELD',

  // Resource
  NOT_FOUND = 'NOT_FOUND',
  ALREADY_EXISTS = 'ALREADY_EXISTS',
  CONFLICT = 'CONFLICT',

  // Network
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT = 'TIMEOUT',
  RATE_LIMIT = 'RATE_LIMIT',

  // Server
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',

  // Client
  CLIENT_ERROR = 'CLIENT_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * Toast notification type
 */
export type ToastType = 'success' | 'error' | 'warning' | 'info';

/**
 * Toast notification interface
 */
export interface Toast {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  duration?: number; // milliseconds, 0 means no auto-dismiss
}

/**
 * Application error interface
 */
export interface AppError {
  id: string;
  message: string;
  code?: string;
  statusCode?: number;
  details?: unknown;
  timestamp: Date;
}

/**
 * Error handler options
 */
export interface ErrorHandlerOptions {
  /**
   * Whether to show toast notifications for errors
   * @default true
   */
  showToast?: boolean;

  /**
   * Whether to log errors to the error store
   * @default true
   */
  logError?: boolean;

  /**
   * Custom error message to display
   */
  customMessage?: string;

  /**
   * Callback function to execute when an error occurs
   */
  onError?: (error: Error | AxiosError) => void;
}

/**
 * Error boundary state
 */
export interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * Type guard to check if error is an AxiosError
 */
export function isAxiosError(error: unknown): error is AxiosError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error &&
    (error as AxiosError).isAxiosError === true
  );
}

/**
 * Type guard to check if error is an Error instance
 */
export function isError(error: unknown): error is Error {
  return error instanceof Error;
}

/**
 * Extract error message from various error types
 */
export function getErrorMessage(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }

  if (isAxiosError(error)) {
    const data = error.response?.data as unknown;

    if (typeof data === 'string') {
      return data;
    }

    if (data && typeof data === 'object') {
      const detail = (data as { detail?: unknown }).detail;
      if (typeof detail === 'string') {
        return detail;
      }
      if (Array.isArray(detail)) {
        const detailMessages = detail
          .map((item) => {
            if (typeof item === 'string') {
              return item;
            }
            if (item && typeof item === 'object') {
              const message =
                (item as { msg?: unknown }).msg ||
                (item as { message?: unknown }).message ||
                (item as { detail?: unknown }).detail;
              return typeof message === 'string' ? message : undefined;
            }
            return undefined;
          })
          .filter((message): message is string => Boolean(message));
        if (detailMessages.length > 0) {
          return detailMessages.join(' ');
        }
      }
      if (detail && typeof detail === 'object') {
        const message =
          (detail as { msg?: unknown }).msg ||
          (detail as { message?: unknown }).message;
        if (typeof message === 'string') {
          return message;
        }
      }

      const message = (data as { message?: unknown }).message;
      if (typeof message === 'string') {
        return message;
      }
      const errorMessage = (data as { error?: unknown }).error;
      if (typeof errorMessage === 'string') {
        return errorMessage;
      }
      const errors = (data as { errors?: Record<string, string[] | string> }).errors;
      if (errors && typeof errors === 'object') {
        const errorMessages = Object.entries(errors)
          .flatMap(([field, messages]) => {
            if (Array.isArray(messages)) {
              return messages.map((msg) => `${field}: ${msg}`);
            }
            if (typeof messages === 'string') {
              return [`${field}: ${messages}`];
            }
            return [];
          })
          .filter((message) => message.length > 0);
        if (errorMessages.length > 0) {
          return errorMessages.join(' ');
        }
      }
    }

    return error.message || 'An unexpected error occurred';
  }

  if (isError(error)) {
    return error.message || 'An unexpected error occurred';
  }

  return 'An unexpected error occurred';
}

/**
 * Extract error code from error
 */
export function getErrorCode(error: unknown): string | undefined {
  if (isAxiosError(error)) {
    const data = error.response?.data as ApiErrorResponse;
    return data?.code;
  }

  return undefined;
}

/**
 * Extract HTTP status code from error
 */
export function getStatusCode(error: unknown): number | undefined {
  if (isAxiosError(error)) {
    return error.response?.status;
  }

  return undefined;
}

/**
 * Check if error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  if (isAxiosError(error)) {
    return !error.response && error.message === 'Network Error';
  }
  return false;
}

/**
 * Check if error is a timeout error
 */
export function isTimeoutError(error: unknown): boolean {
  if (isAxiosError(error)) {
    return error.code === 'ECONNABORTED';
  }
  return false;
}

/**
 * Check if error is a validation error
 */
export function isValidationError(error: unknown): boolean {
  if (isAxiosError(error)) {
    return error.response?.status === HttpStatusCode.UNPROCESSABLE_ENTITY;
  }
  return false;
}

/**
 * Check if error is an authentication error
 */
export function isAuthError(error: unknown): boolean {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    return status === HttpStatusCode.UNAUTHORIZED || status === HttpStatusCode.FORBIDDEN;
  }
  return false;
}

/**
 * Check if error is a server error
 */
export function isServerError(error: unknown): boolean {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    return status !== undefined && status >= 500;
  }
  return false;
}

/**
 * Check if error is a client error
 */
export function isClientError(error: unknown): boolean {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    return status !== undefined && status >= 400 && status < 500;
  }
  return false;
}

/**
 * Check if error is retryable (network, timeout, or server errors)
 */
export function isRetryableError(error: unknown): boolean {
  if (isNetworkError(error) || isTimeoutError(error) || isServerError(error)) {
    return true;
  }

  if (isAxiosError(error)) {
    const status = error.response?.status;
    // 408 Request Timeout, 429 Too Many Requests, 503 Service Unavailable, 504 Gateway Timeout
    return status === 408 || status === 429 || status === 503 || status === 504;
  }

  return false;
}

/**
 * Format validation errors from API response
 */
export function formatValidationErrors(
  error: unknown
): Record<string, string[]> | null {
  if (isAxiosError(error) && isValidationError(error)) {
    const data = error.response?.data as ApiErrorResponse;
    return data?.errors || null;
  }
  return null;
}

/**
 * Create a standardized error object
 */
export function createAppError(
  message: string,
  code?: string,
  statusCode?: number,
  details?: unknown
): AppError {
  return {
    id: `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    message,
    code,
    statusCode,
    details,
    timestamp: new Date(),
  };
}

/**
 * Error severity levels
 */
export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

/**
 * Get error severity based on status code
 */
export function getErrorSeverity(error: unknown): ErrorSeverity {
  const statusCode = getStatusCode(error);

  if (!statusCode) {
    return ErrorSeverity.MEDIUM;
  }

  if (statusCode >= 500) {
    return ErrorSeverity.CRITICAL;
  }

  if (statusCode === HttpStatusCode.UNAUTHORIZED || statusCode === HttpStatusCode.FORBIDDEN) {
    return ErrorSeverity.HIGH;
  }

  if (statusCode >= 400) {
    return ErrorSeverity.MEDIUM;
  }

  return ErrorSeverity.LOW;
}

/**
 * Error context for logging and reporting
 */
export interface ErrorContext {
  url?: string;
  method?: string;
  userId?: string;
  timestamp: Date;
  userAgent: string;
  viewport: {
    width: number;
    height: number;
  };
  componentStack?: string;
}

/**
 * Create error context for reporting
 */
export function createErrorContext(
  componentStack?: string,
  userId?: string
): ErrorContext {
  return {
    url: window.location.href,
    userId,
    timestamp: new Date(),
    userAgent: navigator.userAgent,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    componentStack,
  };
}

/**
 * Error report for sending to backend or error tracking service
 */
export interface ErrorReport {
  error: {
    message: string;
    code?: string;
    statusCode?: number;
    stack?: string;
  };
  context: ErrorContext;
  severity: ErrorSeverity;
}

/**
 * Create a complete error report
 */
export function createErrorReport(
  error: unknown,
  componentStack?: string,
  userId?: string
): ErrorReport {
  const message = getErrorMessage(error);
  const code = getErrorCode(error);
  const statusCode = getStatusCode(error);
  const severity = getErrorSeverity(error);
  const context = createErrorContext(componentStack, userId);

  return {
    error: {
      message,
      code,
      statusCode,
      stack: isError(error) ? error.stack : undefined,
    },
    context,
    severity,
  };
}
